/**
 * Shared deep-link param names, parsers, and rejection reasons.
 * See docs/deep-links-system-design.md.
 *
 * Parsers check shape only (format, length). Whether a value exists for the
 * agency is the feature's job, once the data for that step has loaded.
 */

/** Params every deep link may carry, owned by app startup (§5), not features. */
export const DEEP_LINK_PARAMS = Object.freeze({
    VERSION: 'v',
    AGENCY: 'agency',
    JURISDICTION: 'jur',
});

/** Why a link param was not applied. Kept small so analytics can group them. */
export const DEEP_LINK_REJECT_REASON = Object.freeze({
    INVALID_FORMAT: 'invalid_format',
    NO_DATA: 'no_data',
    NOT_FOUND: 'not_found',
    /** Newer than the latest data; the feature fell back to its latest value. */
    NOT_YET_AVAILABLE: 'not_yet_available',
    /** Applied, but only part of what the link asked for fits one view. */
    PARTIAL: 'partial',
    /** Names more than one thing (a fleet number two vehicles share); none is picked. */
    AMBIGUOUS: 'ambiguous',
    /** The data needed to check the value didn't load, so it can't be called missing. */
    LOAD_FAILED: 'load_failed',
});

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_ID_LENGTH = 64;
const MAX_LIST_LENGTH = 20;

/**
 * @typedef {Object} DeepLinkRejection
 * @property {string} param - The URL param name
 * @property {string} reason - A DEEP_LINK_REJECT_REASON
 * @property {string} [value] - The raw value, for the feature's message
 */

/**
 * Reads one param with a parser. A present but malformed value is recorded as
 * rejected and read as null.
 *
 * @param {URLSearchParams} params
 * @param {string} name
 * @param {(raw: string) => *} parse - Returns null when the value is malformed
 * @param {DeepLinkRejection[]} rejected - Collects rejections
 * @returns {*}
 */
export const readParam = (params, name, parse, rejected) => {
    const raw = params.get(name);
    if (raw == null || raw === '') {
        return null;
    }

    const value = parse(raw.trim());
    if (value == null) {
        rejected.push({ param: name, reason: DEEP_LINK_REJECT_REASON.INVALID_FORMAT, value: raw });
    }

    return value;
};

/** @param {string} raw - YYYY-MM-DD */
export const parseServiceDate = (raw) => {
    if (!DATE_PATTERN.test(raw)) {
        return null;
    }

    const parsed = new Date(`${raw}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === raw ? raw : null;
};

/** @param {string} raw - An opaque identifier */
export const parseId = (raw) => (raw && raw.length <= MAX_ID_LENGTH ? raw : null);

/** @param {string} raw - Comma-separated identifiers */
export const parseIdList = (raw) => {
    const ids = raw.split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length === 0 || ids.length > MAX_LIST_LENGTH || !ids.every(parseId)) {
        return null;
    }

    return ids;
};

const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** @param {string} raw - HH:mm, 24-hour */
export const parseTimeOfDay = (raw) => (TIME_OF_DAY_PATTERN.test(raw) ? raw : null);

/**
 * @param {string} raw - Two comma-separated integers, e.g. `-60,300`
 * @param {number} [limit] - Largest absolute value accepted
 * @returns {[number, number]|null} Ordered low, high
 */
export const parseIntegerPair = (raw, limit = 86400) => {
    const parts = raw.split(',');
    if (parts.length !== 2 || !parts.every((part) => /^-?\d+$/.test(part.trim()))) {
        return null;
    }

    const [low, high] = parts.map((part) => Number(part.trim()));
    return Math.abs(low) <= limit && Math.abs(high) <= limit && low <= high ? [low, high] : null;
};

/**
 * Sets a param when the value is present; leaves defaults and nulls out of the
 * URL so links stay short.
 *
 * @param {URLSearchParams} params
 * @param {string} name
 * @param {*} value
 */
export const setParam = (params, name, value) => {
    if (value == null || value === '') {
        return;
    }

    params.set(name, Array.isArray(value) ? value.join(',') : String(value));
};

/**
 * Starts a link's params with its version and location context.
 *
 * @param {number} version
 * @param {{ agencyId?: string|null, jurisdictionId?: string|null }} location
 * @returns {URLSearchParams}
 */
export const createDeepLinkParams = (version, { agencyId = null, jurisdictionId = null } = {}) => {
    const params = new URLSearchParams();
    params.set(DEEP_LINK_PARAMS.VERSION, String(version));
    setParam(params, DEEP_LINK_PARAMS.AGENCY, agencyId);
    setParam(params, DEEP_LINK_PARAMS.JURISDICTION, jurisdictionId);
    return params;
};

/**
 * Location context a link asks for. Applied once at startup through the
 * location precedence chain (user.operations), never read by features.
 *
 * @param {string} search - A location.search string
 * @returns {{ agencyId: string|null, jurisdictionId: string|null }}
 */
export const readDeepLinkLocation = (search = '') => {
    const params = new URLSearchParams(search);
    const ignored = [];

    return {
        agencyId: readParam(params, DEEP_LINK_PARAMS.AGENCY, parseId, ignored),
        jurisdictionId: readParam(params, DEEP_LINK_PARAMS.JURISDICTION, parseId, ignored),
    };
};

/**
 * Whether two features' vehicle IDs name the same vehicle. Some sources
 * suffix the fleet number ("8307-0" in Trip Tracer's AVL, "8307" in Trip
 * Analyzer and on the live map), so a plain number matches its suffixed form.
 *
 * @param {string|number|null} a
 * @param {string|number|null} b
 * @returns {boolean}
 */
export const isSameVehicleId = (a, b) => {
    if (a == null || b == null || a === '' || b === '') {
        return false;
    }

    const left = String(a);
    const right = String(b);
    return left === right || left.startsWith(`${right}-`) || right.startsWith(`${left}-`);
};

/**
 * The vehicle a link's ID means among the IDs a feature has: the exact ID if
 * it's there, else the one ID that matches it across a suffix
 * (isSameVehicleId). Two or more such IDs are ambiguous ("8307" when both
 * "8307-0" and "8307-1" ran), so none is picked and they come back in
 * `matches` for the message.
 *
 * @param {Array<string|number>} candidateIds - IDs the feature has, in any order; repeats are fine
 * @param {string|number|null} vehicleId - From the link
 * @returns {{ vehicleId: string|null, matches: string[] }}
 */
export const resolveLinkedVehicleId = (candidateIds, vehicleId) => {
    if (vehicleId == null || vehicleId === '') {
        return { vehicleId: null, matches: [] };
    }

    const ids = [...new Set(candidateIds.filter((id) => id != null && id !== '').map(String))];
    const wanted = String(vehicleId);
    if (ids.includes(wanted)) {
        return { vehicleId: wanted, matches: [wanted] };
    }

    const matches = ids
        .filter((id) => isSameVehicleId(id, wanted))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    return { vehicleId: matches.length === 1 ? matches[0] : null, matches };
};

/**
 * Today's service date (YYYY-MM-DD) in a timezone, for links that open "today".
 *
 * @param {string|null} [timeZone] - IANA timezone; the browser's when absent or invalid
 * @param {Date} [now]
 * @returns {string}
 */
export const getServiceDateInTimeZone = (timeZone = null, now = new Date()) => {
    const format = (zone) => new Intl.DateTimeFormat('en-CA', {
        timeZone: zone ?? undefined,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);

    try {
        return format(timeZone);
    } catch {
        return format(null);
    }
};
